/**
 * AI_CAMPAIGN_PROMPT
 *
 * A self-contained instruction block you can paste into any AI agent (ChatGPT,
 * Claude, etc.). The agent will return a single JSON object that can be pasted
 * directly into the Campaign Builder's "Import" dialog.
 *
 * Keep this in sync with docs/campaign-import-json-spec.md and the node config
 * defaults in src/hooks/useCampaignBuilder.js / NodeConfigDrawer.jsx.
 */
export const AI_CAMPAIGN_PROMPT = `You generate automation campaigns for "Origin", a mortgage CRM. Convert the campaign I describe into ONE JSON object that I can paste into Origin's Campaign Builder "Import" dialog.

OUTPUT RULES
- Output ONLY the raw JSON object. No markdown, no code fences, no commentary.
- The JSON must be valid and parseable.

TOP-LEVEL SHAPE
{
  "template": {
    "name": "string (required, short)",
    "description": "string (optional)",
    "triggerType": "one of the trigger types below",
    "triggerConfig": { },
    "enrollmentRules": { "allow_re_enrollment": false }
  },
  "nodes": [ /* array of step objects, see below */ ]
}

HOW THE FLOW WORKS (IMPORTANT)
- The trigger is NOT a node. It lives only on template.triggerType + template.triggerConfig.
- "nodes" is a FLAT array that contains every step, including steps inside branches.
- Steps are connected as a linked list, NOT by array order:
  - "nextNodeId": id of the next step on the main / TRUE / "A" / "condition met" path (null to end the path).
  - "falseNodeId": only used by IF_ELSE, AB_SPLIT, and WAIT_FOR. It points to the first step of the FALSE / "B" / "timed out" branch (null if none).
- A branch is just more nodes in the same array: the branching node's "falseNodeId" points to the first branch step, and those branch steps chain to each other via their own "nextNodeId".
- The first step of the campaign is the node with the lowest "orderIndex" that nothing else points to.

EACH NODE OBJECT
{
  "id": "any unique string (e.g. n1, n2) - Origin regenerates real UUIDs on import",
  "subType": "one of the node types below",
  "nodeType": "ACTION | WAIT | CONDITION (must match the subType's category below)",
  "config": { /* fields depend on subType */ },
  "nextNodeId": "id | null",
  "falseNodeId": "id | null",
  "orderIndex": 0
}

NODE TYPES (subType -> nodeType -> config fields)
Communication:
- SEND_EMAIL -> ACTION -> { "emailField": "email|secondaryEmail|custom", "customEmail": "(only if emailField=custom)", "fromEmail": "(optional)", "replyTo": "(optional)", "subject": "required", "body": "required, may contain HTML" }
- SEND_SMS -> ACTION -> { "phoneField": "phone|secondaryPhone|custom", "customPhone": "(only if phoneField=custom)", "message": "required (unless media set)", "media": null }
- SEND_NOTIFICATION -> ACTION -> { "message": "required", "notifyOwner": true }
CRM actions:
- ADD_NOTE -> ACTION -> { "text": "required" }
- UPDATE_STAGE -> ACTION -> { "stage": "deal stage slug, e.g. lead-contacted" }
- CREATE_TASK -> ACTION -> { "title": "required", "description": "", "priority": "low|medium|high", "dueDays": 1 }
- UPDATE_DEAL_FIELD -> ACTION -> { "fieldName": "amount|product|lender|loan_purpose|property_type|estimated_closing_date|next_action|is_at_risk", "fieldValue": "string" }
- MANAGE_TAG -> ACTION -> { "action": "add|remove", "tagName": "required", "target": "contact|deal|both", "triggerCampaigns": false }  // triggerCampaigns=true makes the tag change fire TAG_ADDED/TAG_REMOVED campaign triggers, exactly like a person tagging by hand; false (default) = silent bookkeeping tag
Logic / timing:
- WAIT -> WAIT -> { "duration": 1, "unit": "minutes|hours|days" }
- WAIT_FOR -> WAIT -> { "field": "(see condition fields)", "operator": "(see operators)", "value": "", "checkInterval": 60, "timeoutDuration": 30, "timeoutUnit": "hours|days", "continueAfterTimeout": false }  // condition met -> nextNodeId, timed out -> falseNodeId
- IF_ELSE -> CONDITION -> { "field": "(see condition fields)", "operator": "(see operators)", "value": "" }  // true -> nextNodeId, false -> falseNodeId
- SWITCH -> CONDITION -> { "field": "(see condition fields)", "cases": [ { "value": "x", "nextNodeId": "id-of-step-for-x" } ], "defaultNextNodeId": "id-or-null" }
- AB_SPLIT -> CONDITION -> { "splitPercent": 50 }  // branch A -> nextNodeId, branch B -> falseNodeId
Integration:
- SEND_WEBHOOK -> ACTION -> { "url": "https://...", "method": "POST|PUT|PATCH", "body": "JSON string", "headers": "(optional JSON string)" }
- APPEND_SHEET_ROW -> ACTION -> { "spreadsheetId": "<Google Sheet ID or URL>", "sheetName": "(optional tab name)", "columns": [{ "label": "Column header", "value": "text with {{merge.tags}}" }] }
- ENROLL_CAMPAIGN -> ACTION -> { "targetCampaignId": "another campaign's template UUID", "ifAlreadyEnrolled": "skip|enroll_again" }  // skip (default) = do nothing if the contact already has an active run in the target campaign; enroll_again = start a second, parallel run
- UNENROLL_CAMPAIGN -> ACTION -> { "targetCampaignId": "another campaign's template UUID" }

CONDITION FIELDS (for IF_ELSE / WAIT_FOR / SWITCH "field")
contact: phone, email, company, tags, source, contact_types
deal: deal.stage, deal.amount, deal.product, deal.lender, deal.property_state, deal.loan_purpose, deal.credit_score, deal.callLoggedSinceReset

OPERATORS
exists, not_exists, equals, not_equals, contains, greater_than, less_than
(when operator is exists / not_exists, omit or empty "value")

TRIGGER TYPES (triggerType -> triggerConfig)
- CONTACT_CREATED -> { "source": "(optional)" }
- CONTACT_UPDATED -> { "fieldName": "(optional)" }
- TAG_ADDED -> { "tagScope": "contact|deal|either", "tagName": "(optional)" }
- TAG_REMOVED -> { "tagScope": "contact|deal|either", "tagName": "(optional)" }
- DEAL_CREATED -> { "source": "(optional)" }
- PIPELINE_STAGE_CHANGED -> { "fromStage": "(optional)", "toStage": "(optional)" }
- DEAL_FIELD_UPDATED -> { "fieldName": "(optional)" }
- DEAL_ASSIGNED -> { }
- DEAL_STALLED -> { "stalledDays": 3 }
- DISPOSITION_LOGGED -> { "disposition": "(optional)" }
- EMAIL_OPENED -> { }
- EMAIL_REPLIED -> { }
- SMS_REPLIED -> { }
- WEBHOOK_RECEIVED -> { }
- SCHEDULE_RECURRING -> { "interval": 1, "period": "days|weeks|months" }
- SCHEDULE_DATE_FIELD -> { "dateField": "birthday|loan_anniversary|estimated_closing_date", "offsetDays": 0, "offsetDirection": "before|after|on" }
- MANUAL_ADD -> { }

MERGE TAGS (usable in any text/subject/body/message/note)
{{contact.firstName}}, {{contact.lastName}}, {{contact.email}}, {{contact.phone}}, {{contact.company}}, {{deal.amount}}, {{deal.stage}}, {{sender.name}}

WORKED EXAMPLE (a valid output)
{
  "template": {
    "name": "New Lead Welcome",
    "description": "Texts new leads, waits a day, then branches on whether they reached the contacted stage.",
    "triggerType": "TAG_ADDED",
    "triggerConfig": { "tagScope": "contact", "tagName": "new-lead" },
    "enrollmentRules": { "allow_re_enrollment": false }
  },
  "nodes": [
    { "id": "n1", "subType": "SEND_SMS", "nodeType": "ACTION", "config": { "phoneField": "phone", "message": "Hi {{contact.firstName}}, thanks for reaching out! A loan officer will be in touch shortly." }, "nextNodeId": "n2", "falseNodeId": null, "orderIndex": 0 },
    { "id": "n2", "subType": "WAIT", "nodeType": "WAIT", "config": { "duration": 1, "unit": "days" }, "nextNodeId": "n3", "falseNodeId": null, "orderIndex": 1 },
    { "id": "n3", "subType": "IF_ELSE", "nodeType": "CONDITION", "config": { "field": "deal.stage", "operator": "equals", "value": "lead-contacted" }, "nextNodeId": "n4", "falseNodeId": "n5", "orderIndex": 2 },
    { "id": "n4", "subType": "SEND_EMAIL", "nodeType": "ACTION", "config": { "emailField": "email", "subject": "Great talking with you, {{contact.firstName}}", "body": "<p>Here are your next steps...</p>" }, "nextNodeId": null, "falseNodeId": null, "orderIndex": 3 },
    { "id": "n5", "subType": "CREATE_TASK", "nodeType": "ACTION", "config": { "title": "Call {{contact.firstName}} - no contact yet", "description": "Follow up on the new lead.", "priority": "high", "dueDays": 1 }, "nextNodeId": null, "falseNodeId": null, "orderIndex": 4 }
  ]
}

Now build this campaign:
<DESCRIBE YOUR CAMPAIGN HERE>`;

export default AI_CAMPAIGN_PROMPT;
